import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ShiftRequest } from "../../../utils/models";
import { CoachMark, coachMarkTargetSx } from "../../UIToolkit/molecules/CoachMark";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { OutreachPopupContainer } from "./OutreachPopupContainer";
import { DATE_FORMAT, timeRangeOf } from "./useOutreachCopy";

/** The first-open highlight on one card's button; `null` once the user has opened the popup. */
export type OutreachCoachMark = { dismiss: () => void } | null;

type Props = { request: ShiftRequest; coachMark: OutreachCoachMark };

/** The "Who we're asking" button on a request card, and the popup it opens. */
export const RequestOutreachEntry: FC<Props> = ({ request, coachMark }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const label = t("request.outreach.open");
	const { shift_start: start, shift_end: end } = request.message;

	return (
		<>
			<CoachMark label={label} open={coachMark !== null} align="end" dataTestId={`request-outreach-coach-mark-${request.id}`}>
				<SystemButton
					data-testid={`request-outreach-${request.id}`}
					iconButton
					tooltip={coachMark === null ? label : undefined}
					aria-label={t("request.outreach.open_aria", { date: start.toFormat(DATE_FORMAT), time: timeRangeOf(start, end) })}
					leadingIcon={<InfoOutlinedIcon fontSize="small" />}
					sx={coachMark === null ? undefined : coachMarkTargetSx}
					onClick={(event) => {
						event.stopPropagation();
						coachMark?.dismiss();
						setOpen(true);
					}}
				/>
			</CoachMark>
			{open && (
				// The popup portals out of the DOM but not out of the React tree, so its clicks still bubble to the request
				// card's own onClick (which opens the approval modal). Stop them here.
				<Box component="span" sx={{ display: "contents" }} onClick={(event) => event.stopPropagation()}>
					<OutreachPopupContainer request={request} onClose={() => setOpen(false)} />
				</Box>
			)}
		</>
	);
};
